import { Component, inject, Input, OnInit, OnDestroy } from '@angular/core'
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Guid } from 'guid-typescript'
import { parse } from 'postcode'
import { ModalController } from '@ionic/angular/standalone'
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog'
import { Subscription } from 'rxjs/internal/Subscription'
import { map, Observable } from 'rxjs'
import { AuthService } from 'src/app/core/auth.service'
import { FileModel } from 'src/app/models/file.model'
import { BuildingCollection, Collection, CollectionType, CustomerCollection, SubCollection } from 'src/app/models/firedna-collections.model'
import { FloorplateModel } from 'src/app/models/floorplate.model'
import { Profile } from 'src/app/models/profile.model'
import { Transaction } from 'src/app/models/transactions'
import { DatastoreCollectionsService } from 'src/app/services/datastore-collections.service'
import { DatastoreEntityService } from 'src/app/services/datastore-entity.service'
import { OfflineTransactionsService } from 'src/app/services/offline-transactions.service'
import { config } from 'src/environments/environment'
import { ImportExportModalComponent } from 'src/app/modals/import-export-modal/import-export-modal.component'
import { AddSiteModalComponent } from 'src/app/modals/add-site-modal/add-site-modal.component'

@Component({
  selector: 'app-create-building',
  templateUrl: './create-building.page.html',
  styleUrls: ['./create-building.page.scss'],
  standalone: false,
})
export class CreateBuildingPage implements OnInit, OnDestroy {
  @Input() collectionType: CollectionType = new CollectionType()
  @Input() profile: Profile
  public form: FormGroup
  public floorForm: FormGroup = new FormGroup({})
  private sub: Subscription = new Subscription()
  foundBuildings: BuildingCollection[] = []
  selectedFoundBuilding: BuildingCollection
  selectedFoundAddress: string = ''
  editMode: boolean = false
  floors: string[] = ['']

  btnProcessing: boolean = false
  sites$: Observable<string[]>

  config = inject(DynamicDialogConfig)

  constructor(
    private datastoreCollections: DatastoreCollectionsService,
    private datastoreEntityService: DatastoreEntityService,
    private dialogService: DialogService,
    public auth: AuthService,
    public modalController: ModalController,
    private offlineTransactionsService: OfflineTransactionsService,
    public formBuilder: FormBuilder,
    private dialogRef: DynamicDialogRef
  ) {
    this.floorForm = new FormGroup({
      data: new FormArray([]),
    })
    this.form = formBuilder.group({
      name: ['', Validators.required],
      primaryContact: [''],
      telephone: [''],
      reference: [''],
      email: [''],
      address: ['', Validators.required],
      postcode: ['', Validators.required],
      site: [],
    })

    // Load sites from entity data
    this.sites$ = this.datastoreEntityService.entity$.pipe(map((entity) => entity.entityData?.sites || []))
  }

  ngOnInit() {
    if (this.config.data && this.config.data.collectionType) {
      this.collectionType = this.config.data.collectionType
      this.profile = this.config.data.profile
    }
    console.log(this.collectionType)
    this.form.valueChanges.subscribe(() => {
      console.log('change')
    })
    console.log(this.collectionType)
  }

  ngOnDestroy() {
    this.sub.unsubscribe()
  }

  openAddSiteModal() {
    const ref = this.dialogService.open(AddSiteModalComponent, {
      header: 'Add New Site',
      width: '400px',
      modal: true,
    })

    this.sub.add(
      ref.onClose.subscribe((result: { siteName: string } | null) => {
        if (result?.siteName) {
          // Set the newly created site as selected
          this.form.controls['site'].setValue(result.siteName)
        }
      })
    )
  }

  add() {
    this.getDataArray().push(new FormControl('Floor ' + (this.getDataArray().length + 1), Validators.required))
  }

  delete(i: number) {
    this.getDataArray().removeAt(i)
  }

  getDataArray() {
    return this.floorForm.get('data') as FormArray
  }

  async import() {
    this.dismiss()
    const modal = await this.modalController.create({
      component: ImportExportModalComponent,
      componentProps: {},
    })

    modal.onDidDismiss().then(() => {
      // this.getData()
    })
    return await modal.present()
  }

  async create() {
    console.log(this.floorForm.value)
    if (this.form.valid && this.floorForm.valid) {
      this.btnProcessing = true
      const transactions: Transaction[] = []
      const collectiondata = this.getCollectionData()

      const newCollection = new Collection()

      newCollection.id = Guid.create().toString()
      newCollection.collectionName = this.form.value.name
      newCollection.collectionData = collectiondata
      newCollection.collectionType = this.collectionType.collection.singular
      newCollection.ownerId = this.profile.entityId
      newCollection.parentId = null
      newCollection.collectionStatus = 'active'
      newCollection.subCollections = []
      newCollection.documents = []
      newCollection.localCache = 0
      newCollection.priority = 0

      const body = []

      body.push({
        key: 'Id',
        value: newCollection.id,
      })
      body.push({
        key: 'CollectionName',
        value: newCollection.collectionName,
      })
      body.push({
        key: 'OwnerId',
        value: newCollection.ownerId,
      })
      body.push({
        key: 'CollectionType',
        value: newCollection.collectionType,
      })
      body.push({
        key: 'CollectionStatus',
        value: newCollection.collectionStatus,
      })
      body.push({
        key: 'CollectionData',
        value: JSON.stringify(newCollection.collectionData),
      })

      const transaction: Transaction = new Transaction()
      transaction.action = 'post'
      transaction.url = config.datastoreCollectionsApi
      transaction.data = body
      transaction.version = '1.1'

      transactions.push(transaction)

      console.log(newCollection)

      await this.datastoreCollections.createCollection(newCollection)

      this.getDataArray().controls.forEach(async (control) => {
        // console.log(control, control.value)

        const subCollection: SubCollection = new SubCollection()

        subCollection.id = Guid.create().toString()
        subCollection.collectionName = control.value
        subCollection.ownerId = this.profile.entityId
        subCollection.parentId = newCollection.id
        subCollection.collectionType = this.collectionType.subCollection.singular
        subCollection.combinedProducts = []
        // subCollection.collectionData = {};
        subCollection.collectionStatus = 'active'
        subCollection.floorplateImages = new FloorplateModel()
        subCollection.floorplateImages!.floorplateImage = new FileModel()

        const floorBody = []

        floorBody.push({
          key: 'Id',
          value: subCollection.id,
        })
        floorBody.push({
          key: 'CollectionName',
          value: subCollection.collectionName,
        })
        floorBody.push({
          key: 'OwnerId',
          value: subCollection.ownerId,
        })
        floorBody.push({
          key: 'ParentId',
          value: subCollection.parentId,
        })
        floorBody.push({
          key: 'CollectionType',
          value: subCollection.collectionType,
        })
        floorBody.push({
          key: 'CollectionStatus',
          value: subCollection.collectionStatus,
        })

        const floorTransaction: Transaction = new Transaction()
        floorTransaction.action = 'post'
        floorTransaction.url = config.datastoreCollectionsApi
        floorTransaction.data = floorBody

        transactions.push(floorTransaction)

        await this.datastoreCollections.createSubCollection(subCollection)
        // console.log(subCollection);
      })

      await this.offlineTransactionsService.addOfflineTransactions(transactions)
      this.dismiss()
    } else {
      this.form.markAllAsTouched()
      for (const i in this.form.controls) {
        this.form.controls[i].setValue(this.form.controls[i].value)
        this.form.controls[i].markAsDirty()
      }
    }
  }

  getCollectionData(): CustomerCollection {
    const postcode = parse(this.form.controls.postcode.value!)

    return {
      name: this.form.controls.name.value,
      primaryContact: this.form.controls.primaryContact.value,
      address: this.form.controls.address.value,
      telephone: this.form.controls.telephone.value,
      email: this.form.controls.email.value,
      reference: this.form.controls.reference.value,
      logo: [],
      postcode: postcode.postcode !== null ? postcode.postcode : this.form.controls.postcode.value,
      tags: [],
      latLng: undefined,
      site: this.form.controls.site.value || undefined,
    }
  }
  hasRole(role: string) {
    if (this.auth.hasRole(role)) {
      return true
    } else {
      return false
    }
  }

  dismiss() {
    this.dialogRef.close('close')
  }
}
